import { useState, useEffect } from 'react'
import { useConfessions } from '../hooks/useConfessions.js'
import { usePullToRefresh } from '../hooks/usePullToRefresh.js'
import { useHiddenPosts } from '../hooks/useHiddenPosts.js'
import ConfessionCard from '../components/ConfessionCard.jsx'

const CATEGORIES = ['All', 'Love', 'Work', 'Family', 'Friendship', 'Prayer', 'Other']
const SORTS = [
  { value: 'new', label: 'New' },
  { value: 'top', label: 'Top' },
  { value: 'controversial', label: 'Controversial' },
  { value: 'hidden', label: 'Hidden' },
]

// focusTarget: { id, type: 'karma' | 'comment', nonce } aus einer angetippten Push-Nachricht
export default function FeedPage({ onPin, focusTarget, onOpenPost }) {
  const [sort, setSort] = useState('new')
  const [category, setCategory] = useState('')
  const [activeFocus, setActiveFocus] = useState(null)

  const { hiddenIds, toggleHidden } = useHiddenPosts()
  const { confessions, loading, error, vote, judge, pray, report, ensure, refresh } = useConfessions(sort, category, hiddenIds)
  const showHidden = sort === 'hidden'
  // Im Hidden-Reiter verschwinden wieder eingeblendete Geständnisse direkt
  const visible = showHidden ? confessions.filter(c => hiddenIds.includes(c.id)) : confessions
  const { pull, refreshing, ready } = usePullToRefresh(refresh)

  // Erst schnell neu laden, dann zum Geständnis springen
  useEffect(() => {
    if (!focusTarget) return
    let cancelled = false
    ;(async () => {
      await refresh()
      const found = await ensure(focusTarget.id)
      if (!cancelled && found) setActiveFocus(focusTarget)
    })()
    return () => { cancelled = true }
  }, [focusTarget]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <main
      className="feed-page"
      style={pull ? { transform: `translateY(${pull}px)`, transition: 'none' } : undefined}
    >
      {(pull > 0 || refreshing) && (
        <div
          className="pull-refresh"
          style={{ top: `${-Math.max(pull, refreshing ? 44 : 0) + 8}px` }}
        >
          {refreshing
            ? <span className="spinner" aria-label="Reloading…" />
            : <span className={`pull-refresh-arrow ${ready ? 'ready' : ''}`}>↓</span>}
        </div>
      )}
      <div className="feed-controls">
        {/* Sortierung */}
        <div className="sort-tabs" role="tablist">
          {SORTS.map(s => (
            <button
              key={s.value}
              role="tab"
              aria-selected={sort === s.value}
              className={`sort-tab ${sort === s.value ? 'active' : ''}`}
              onClick={() => setSort(s.value)}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Kategorie-Filter */}
        <div className="category-filter">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              className={`category-chip ${(cat === 'All' ? '' : cat) === category ? 'active' : ''}`}
              onClick={() => setCategory(cat === 'All' ? '' : cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {loading && !refreshing && (
        <div className="feed-state">
          <div className="spinner" aria-label="Loading…" />
        </div>
      )}

      {error && (
        <div className="feed-state feed-error" role="alert">
          {error}
        </div>
      )}

      {!loading && !error && visible.length === 0 && (
        <div className="feed-state feed-empty">
          {showHidden
            ? 'No hidden confessions. Tap the eye on a post to hide it.'
            : 'No confessions yet. Be the first!'}
        </div>
      )}

      <div className="confessions-grid">
        {visible.map(confession => (
          <ConfessionCard
            key={confession.id}
            confession={confession}
            onVote={vote}
            onJudge={judge}
            onPray={pray}
            onReport={report}
            onPin={onPin}
            focus={activeFocus?.id === confession.id ? activeFocus : null}
            hidden={hiddenIds.includes(confession.id)}
            showWhenHidden={showHidden}
            onToggleHidden={toggleHidden}
            onOpen={onOpenPost}
          />
        ))}
      </div>
    </main>
  )
}
